import { useCartContext } from 'components/globalContext/CartContext';
import Button from 'components/helpers/Button';
import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper';
import Search from 'components/authorable/site/Header/SvgSearch/search';
import { classnames } from 'tailwindcss-classnames';
import { pageorLink } from 'components/authorable/site/Footer/Footer';
import NextLink from 'next/link';
import {
  ImageField,
  LinkField,
  Image as SitecoreImage,
  useSitecoreContext,
  Field,
} from '@sitecore-jss/sitecore-jss-react';
import {
  pageNavigationDataLayerHandler,
  menuLocation,
  logoNavigationGtmPush,
} from 'lib/util/gtm-datalayer';
import { utilityLinkClass } from './NmdpSiteHeaderClasses';

type DesktopTopContainerProps = {
  placeholderText: string;
  logo: ImageField;
  utilityLinks: pageorLink[];
  logoText: string;
  desktopItems: React.ReactNode;
  giveMoneyButton: LinkField;
  joinRegistryButton: LinkField;
};

const menuBar = classnames(
  'border-0',
  'font-medium',
  'justify-evenly',
  'justify-start',
  'leading-3',
  'text-base',
  'text-white',
  'xl:flex'
);

const DesktopNavigation = ({ desktopItems }: { desktopItems: React.ReactNode }) => {
  return (
    <ul
      className={classnames(menuBar, 'max-w-screen-2xl', 'xl:mr-auto', 'relative')}
      role="menubar"
      aria-label="Network Resource Center main menu"
    >
      {desktopItems}
    </ul>
  );
};

const DesktopTopContainer = ({
  logo,
  placeholderText,
  utilityLinks,
  logoText,
  desktopItems,
  giveMoneyButton,
  joinRegistryButton,
}: DesktopTopContainerProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const cartLink = sitecoreContext?.cartLink as LinkField;
  const { cartContext } = useCartContext();
  const cartLinkwithCount = {
    ...cartLink?.value,
    text: `${cartLink?.value?.text || ''} (${cartContext})`,
  };
  return (
    <>
      <div className={classnames('border-b-2', 'border-gray-whisper')}>
        <ul
          className={classnames(
            'hidden',
            'xl:flex',
            'xl:relative',
            'xl:flex-row',
            'justify-end',
            'w-full',
            'xl:items-center',
            'p-2'
          )}
        >
          {cartContext > 0 && cartLink?.value?.href?.length && (
            <li>
              <LinkA11yWrapper
                field={{ value: cartLinkwithCount }}
                className={classnames(utilityLinkClass, 'xl:px-4')}
              />
            </li>
          )}
          {utilityLinks?.map((item) => (
            <li key={item.name} className={classnames('list-none')} aria-label="Menu item">
              <LinkA11yWrapper
                className={classnames(utilityLinkClass, 'xl:px-4')}
                editable={false}
                suppressNewTabIcon={false}
                field={
                  item.fields.navigationItemLink
                    ? (item.fields.navigationItemLink as LinkField)
                    : {
                        value: {
                          href: item.url,
                          text: (item.fields.pageTitle as Field<string>).value
                            ? (item.fields.pageTitle as Field<string>).value
                            : item.displayName,
                        },
                      }
                }
                onGtmPush={(e) => {
                  pageNavigationDataLayerHandler(e, menuLocation[0]);
                }}
              />
            </li>
          ))}
        </ul>
      </div>
      <nav
        aria-label="Nmdp Website utility navigation"
        className={classnames(
          'absolute',
          'flex-col',
          'hidden',
          'mx-auto',
          'md:my-4',
          'w-full',
          'xl:flex',
          'xl:flex-row',
          'xl:h-12',
          'xl:items-center',
          'xl:mx-auto',
          'xl:relative'
        )}
      >
        <div
          className={classnames(
            'flex',
            'flex-col',
            'w-full',
            'xl:flex-row',
            'xl:h-12',
            'xl:items-center',
            'xl:justify-between',
            'xl:mx-auto',
            'xl:pl-4',
            'xl:pr-4'
          )}
        >
          <div className={classnames('flex')}>
            <NextLink href="/" locale={false}>
              <a
                href="/"
                aria-label="Menu item"
                onClick={() => {
                  logoNavigationGtmPush(menuLocation[1]);
                }}
              >
                <SitecoreImage field={logo} className="logo-desktop" />
              </a>
            </NextLink>
            {logoText !== '' && (
              <div className={classnames('flex', 'items-center', 'ml-2')}>
                <NextLink href="/" locale={false}>
                  <a
                    href="/"
                    className={classnames('text-blue-navy', 'font-bold', 'font-dmSans', 'text-xl')}
                  >
                    {logoText}
                  </a>
                </NextLink>
              </div>
            )}
          </div>
          <DesktopNavigation desktopItems={desktopItems} />
          <div
            className={classnames(
              'relative',
              'bg-gradient-to-r',
              'from-blue',
              'to-green',
              'p-0',
              'xl:bg-none',
              'xl:pr-2.5'
            )}
          >
            <Search searchplaceholderText={placeholderText} />
          </div>
          {giveMoneyButton?.value && (
            <div>
              <Button
                linkField={giveMoneyButton}
                className={classnames('lg:mr-2', 'px-0', 'text-sm', 'lg:pt-2')}
                onGtmPush={(e) => {
                  pageNavigationDataLayerHandler(e);
                }}
              />
            </div>
          )}
          {joinRegistryButton?.value && (
            <div>
              <Button
                linkField={joinRegistryButton}
                className={classnames('lg:mr-2', 'px-0', 'text-sm', 'lg:pt-2')}
                onGtmPush={(e) => {
                  pageNavigationDataLayerHandler(e);
                }}
              />
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default DesktopTopContainer;
